import { Home, Users, Clock, CalendarClock, User, Settings, LogOut, BarChart, Shield, FileText, CreditCard } from "lucide-react";
import { Link } from "react-router-dom";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useAuthStore } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { TemplatesDialog } from "@/components/templates/TemplatesDialog";

interface MenuItem {
  title: string;
  id: string;
  icon: React.ElementType;
  url: string;
  roles: string[];
}

interface SidebarMenuItemsProps {
  activeItem: string;
  setActiveItem: (item: string) => void;
}

export function SidebarMenuItems({ activeItem, setActiveItem }: SidebarMenuItemsProps) {
  const { user, logout } = useAuthStore();
  const { toast } = useToast();
  const [showTemplatesDialog, setShowTemplatesDialog] = useState(false);

  // Define menu items based on user role
  const menuItems: MenuItem[] = [
    {
      title: "Dashboard",
      id: "dashboard",
      icon: Home,
      url: "/",
      roles: ['admin', 'staff'],
    },
    {
      title: "Registration",
      id: "registration",
      icon: User,
      url: "/registration",
      roles: ['staff'],
    },
    {
      title: "Queue",
      id: "queue",
      icon: Users,
      url: "/queue",
      roles: ['admin', 'staff'],
    },
    {
      title: "Follow-ups",
      id: "followups",
      icon: CalendarClock,
      url: "/followups",
      roles: ['staff'],
    },
    {
      title: "History",
      id: "history",
      icon: Clock,
      url: "/history",
      roles: ['admin', 'staff'],
    },
    {
      title: "Analytics",
      id: "analytics",
      icon: BarChart,
      url: "/analytics",
      roles: ['admin'],
    },

    {
      title: "Settings",
      id: "settings",
      icon: Settings,
      url: "/settings",
      roles: ['admin'],
    },
    {
      title: "Subscriptions",
      id: "subscription",
      icon: CreditCard,
      url: "/subscription",
      roles: ['admin'],
    },
    {
      title: "Super Admin",
      id: "super-admin",
      icon: Shield,
      url: "/super-admin",
      roles: ['super_admin'],
    },
    {
      title: "Templates",
      id: "templates",
      icon: FileText,
      url: "#",
      roles: ['doctor'],
    },
  ];
  
  // Filter menu items based on user role
  const filteredMenuItems = user 
    ? menuItems.filter(item => item.roles.includes(user.role))
    : [];
  
  const handleLogout = async () => {
    try {
      // Show logout toast immediately
      toast({
        title: "Logging out...",
        description: "Please wait while we log you out",
      });
      
      await logout();
      
      toast({
        title: "Logged out successfully",
        description: "You have been logged out of your account",
      });
      
      // Force hard navigation to login page to prevent auto-login
      setTimeout(() => {
        // Clear any remaining auth state
        localStorage.clear();
        sessionStorage.clear();
        
        // Force navigation with cache busting
        window.location.replace('/login?logout=' + Date.now());
      }, 500);
      
    } catch (error) {
      console.error('Logout error:', error);
      toast({
        title: "Logout error",
        description: "There was an error logging out. Please try again.",
        variant: "destructive",
      });
      
      // Force navigation even on error
      setTimeout(() => {
        localStorage.clear();
        sessionStorage.clear();
        window.location.replace('/login?error=' + Date.now());
      }, 1000);
    }
  };

  const handleMenuItemClick = (item: MenuItem) => {
    setActiveItem(item.id);
    if (item.id === 'templates') {
      setShowTemplatesDialog(true);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <SidebarMenu className="flex-1 space-y-1">
        {filteredMenuItems.map((item) => (
          <SidebarMenuItem
            key={item.id}
            data-active={activeItem === item.id}
            onClick={() => handleMenuItemClick(item)}
            className="px-3 py-2.5 rounded-lg transition-all duration-300 hover:bg-gray-100/50 hover:text-gray-900 data-[active=true]:bg-gradient-to-r data-[active=true]:from-blue-600 data-[active=true]:to-blue-700 data-[active=true]:text-white data-[active=true]:shadow-lg data-[active=true]:font-semibold data-[active=true]:scale-[1.02] data-[active=true]:ring-2 data-[active=true]:ring-blue-400/50"
          >
            {item.id === 'templates' ? (
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setActiveItem(item.id);
                  setShowTemplatesDialog(true);
                }}
                className="flex items-center gap-3 w-full text-left"
              >
                <item.icon className="h-5 w-5" />
                <span className="font-medium">{item.title}</span>
              </button>
            ) : (
            <Link to={item.url} className="flex items-center gap-3 w-full">
              <item.icon className="h-5 w-5" />
              <span className="font-medium">{item.title}</span>
            </Link>
            )}
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
      
      <div className="mt-auto pt-4 border-t">
        <SidebarMenuButton 
          onClick={handleLogout}
          className="px-3 py-2 rounded-md transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          <LogOut className="h-5 w-5" />
          <span className="font-medium">Logout</span>
        </SidebarMenuButton>
      </div>

      {/* Templates Dialog */}
      <TemplatesDialog
        isOpen={showTemplatesDialog}
        onClose={() => setShowTemplatesDialog(false)}
      />
    </div>
  );
}
